import React, { useEffect, useState, useRef } from 'react'
import { connect } from 'react-redux'
import { useHistory } from 'react-router'
import CryptoJS from 'crypto-js' // Required for hashing
import { Info } from '@material-ui/icons'

import renderImage from '../../source/importImg'
import AdvanceSettingStyle from './AdvanceSetting.module.css'
import cacheAssets from '../../utils/cacheAssets'

// Components
import AssetUpdateDialog from '../ReusableComponents/AssetUpdateDialog/AssetUpdateDialog'
import FirmwareAssetsUpdateModal from '../ReusableComponents/FirmwareAssetsUpdateModal/FirmwareAssetsUpdateModal'
import WebAgentDialog from '../ReusableComponents/WebAgentDialog/WebAgentDialog'
import FirmwareUpdateDialog from '../ReusableComponents/FirmwareUpdateDialog/FirmwareUpdateDialog' // NEW
import DialogModal from '../ReusableComponents/DialogModal/DialogModal' // NEW (For Boot instructions)

// Utils and Logic
import {
    downloadWebAgent,
    checkSocketAvailability,
} from '../../utils/manualUpdateHelpers'
import { checkAndUpdateMetaYaml } from '../../utils/readWriteFile'
import { webSerialDisconnect } from '../../redux/actions'
import { Transport } from '../FirmwareJS/webserial' // NEW
import { ESPLoader } from '../FirmwareJS/ESPLoader' // NEW
import {
    checkFirmwareVersion,
    fetchFirmwareFiles,
} from '../../utils/firmwareUpdateFlow' // NEW
import { getVersionObjNameKey } from '../../utils/verionControl' // NEW
import { getLogo, getBrand, getSplashConfig } from '../../hooks/useBrandConfig'
import { SERIAL_PORT_FILTERS } from '../../utils/usbDeviceIds'

function AdvanceSettingV1(props) {
    let history = useHistory()
    const version = sessionStorage.getItem('deviceVersion')
    const connectedDevice = sessionStorage.getItem('connectedDevice')

    const [activeSwipeBtn, setActiveSwipeBtn] = useState(false)

    // Logic to determine features
    const isHumanoid2 =
        connectedDevice == 'Humanoid' &&
        (version?.startsWith('2') || version?.startsWith('3'))
    // Logic to determine features
    const isHumanoid3 =
        connectedDevice == 'Humanoid' && version?.startsWith('3')

    // Refs
    const isMountedRef = useRef(true)
    const prevAgentConnectedRef = useRef(props.socketData?.isAgentConnected)
    const timeoutRef = useRef(null)
    const firmwareUpdateResolveRef = useRef(null) // NEW
    const cardRowRef = useRef(null)
    const touchStartXRef = useRef(0)

    useEffect(() => {
        isMountedRef.current = true
        return () => {
            isMountedRef.current = false
            if (timeoutRef.current) clearTimeout(timeoutRef.current)
        }
    }, [])

    // --- HORIZONTAL SCROLL HANDLERS (Mouse Wheel & Touch Swipe) ---
    const handleMouseWheel = (e) => {
        if (!cardRowRef.current) return

        if (e.deltaY !== 0) {
            e.preventDefault()
            const scrollAmount = e.deltaY > 0 ? 100 : -100
            cardRowRef.current.scrollLeft += scrollAmount
        }
    }

    const handleTouchStart = (e) => {
        touchStartXRef.current = e.touches[0].clientX
    }

    const handleTouchEnd = (e) => {
        if (!cardRowRef.current) return
        const touchEndX = e.changedTouches[0].clientX
        const diff = touchStartXRef.current - touchEndX

        if (Math.abs(diff) > 50) {
            const scrollAmount = diff > 0 ? 100 : -100
            cardRowRef.current.scrollLeft += scrollAmount
        }
    }

    // Setup scroll listeners
    useEffect(() => {
        const cardRow = cardRowRef.current
        if (!cardRow) return

        cardRow.addEventListener('wheel', handleMouseWheel, { passive: false })
        cardRow.addEventListener('touchstart', handleTouchStart)
        cardRow.addEventListener('touchend', handleTouchEnd)

        return () => {
            cardRow.removeEventListener('wheel', handleMouseWheel)
            cardRow.removeEventListener('touchstart', handleTouchStart)
            cardRow.removeEventListener('touchend', handleTouchEnd)
        }
    }, [])

    // --- SOCKET MONITORING ---
    useEffect(() => {
        const wasDisconnected = !prevAgentConnectedRef.current
        const isNowConnected = props.socketData?.isAgentConnected

        if (
            wasDisconnected &&
            isNowConnected &&
            props.webAgentDialog?.isInstalling &&
            props.webAgentDialog?.isOpen
        ) {
            console.log('Web Agent connected successfully!')
            if (timeoutRef.current) clearTimeout(timeoutRef.current)

            timeoutRef.current = setTimeout(() => {
                if (isMountedRef.current) {
                    props.hideWebAgentDialog()
                }
            }, 2500)
        }
        prevAgentConnectedRef.current = isNowConnected
    }, [
        props.socketData?.isAgentConnected,
        props.webAgentDialog?.isInstalling,
        props.webAgentDialog?.isOpen,
    ])

    // --- STATE ---

    // Asset Update State
    const [showAssetUpdateDialog, setShowAssetUpdateDialog] = useState(false)
    const [firmwareProgress, setFirmwareProgress] = useState({
        isOpen: false,
        stage: '',
        message: '',
        percent: null,
    })

    // Firmware Update Dialog State (Boot mode instructions, connecting, etc)
    const [dialogState, setDialogState] = useState({
        showDialog: false,
        showConnecting: false,
        showBootModeDialog: false,
        showReconnectDialog: false,
        text: '',
        bootModeOptions: [],
        reconnectOptions: [],
    })

    // Update availability state for notification badges
    const [updateAvailability, setUpdateAvailability] = useState({
        firmwareUpdate: false,
        webAgent: false,
        assetUpdate: false,
    })

    // --- NAVIGATION HANDLERS ---
    const gobackUrl = () => {
        history.goBack()
    }
    const AccountSetting = () => {
        history.push('/accountSetting')
    }
    const aboutClick = () => {
        history.push('/about')
    }
    const operationModeHandel = () => {
        history.push('/operationMode')
    }
    const personaClick = () => {
        history.push('/persona-setting')
    }

    // --- FIRMWARE UPDATE LOGIC (NEW) ---

    const handleFirmwareCheck = async () => {
        const currentDevice = sessionStorage.getItem('connectedDevice')
        const currentVersion = sessionStorage.getItem('deviceVersion')

        if (!currentDevice) {
            alert('No device connected.')
            return
        }

        // 1. Check for updates
        const firmwareCheck = await checkFirmwareVersion(
            getVersionObjNameKey(currentDevice),
            currentVersion
        )

        // 2. Show the Update Dialog (Even if up to date, user might want to force re-flash)
        props.showFirmwareUpdateDialog({
            currentVersion: firmwareCheck.currentVersion,
            latestVersion: firmwareCheck.latestVersion,
            deviceName: firmwareCheck.deviceName,
            firmwareCheck: firmwareCheck, // Pass full object for paths
        })
    }

    // Triggered when user clicks "Update" in the FirmwareUpdateDialog
    const handleFirmwareUpdateStart = async () => {
        if (!isMountedRef.current) return

        const firmwareCheck = props.firmwareUpdateDialog.firmwareCheck
        props.hideFirmwareUpdateDialog()

        // Start the flash process
        await performFirmwareUpdate(firmwareCheck)
    }

    const handleFirmwareUpdateSkip = () => {
        props.hideFirmwareUpdateDialog()
    }

    // Closing the dialog disconnects the device.
    const handleFirmwareUpdateClose = () => {
        props.hideFirmwareUpdateDialog()
        if (props.worker) props.worker.postMessage({ type: 'disconnected' })
        props.webSerialDisconnect()
    }

    const performFirmwareUpdate = async (firmwareCheck) => {
        console.log('=== FIRMWARE UPDATE STARTED ===')
        sessionStorage.setItem('firmwareUpdateInProgress', 'true')
        let bootModeResolve = null

        // 1. Show Boot Mode Instructions
        setDialogState({
            showBootModeDialog: true,
            text: `Please disconnect your device and reconnect it in BOOT MODE.\n\nFor ${firmwareCheck.deviceName}:\n1. Disconnect the device\n2. Press and HOLD the BOOT button\n3. Connect the USB cable while holding BOOT\n4. Release the BOOT button\n\nClick "Ready" when connected in boot mode.`,
            bootModeOptions: [
                {
                    text: 'Ready',
                    do: () => {
                        setDialogState((prev) => ({
                            ...prev,
                            showBootModeDialog: false,
                        }))
                        if (bootModeResolve) bootModeResolve(true)
                    },
                },
                {
                    text: 'Cancel',
                    do: () => {
                        setDialogState((prev) => ({
                            ...prev,
                            showBootModeDialog: false,
                        }))
                        if (bootModeResolve) bootModeResolve(false)
                    },
                },
            ],
        })

        const bootModeConfirmed = await new Promise((resolve) => {
            bootModeResolve = resolve
        })
        if (!bootModeConfirmed) {
            sessionStorage.removeItem('firmwareUpdateInProgress')
            return
        }

        try {
            // 2. Request Port
            setDialogState({
                showConnecting: true,
                text: 'Please select the device (Port) from the browser popup...',
            })

            let device
            try {
                device = await navigator.serial.requestPort({
                    filters: SERIAL_PORT_FILTERS,
                })
            } catch (portError) {
                sessionStorage.removeItem('firmwareUpdateInProgress')
                setDialogState({ showConnecting: false, text: '' })
                return
            }

            const baudrate = 460800

            // 3. Ensure Port is Closed (Fix for Double Open)
            try {
                if (device.readable) {
                    await device.close()
                }
            } catch (e) {
                console.warn(e)
            }

            setDialogState({
                showConnecting: true,
                text: 'Connecting to device...',
            })

            let transport = new Transport(device)
            let esploader = new ESPLoader(transport, baudrate)

            // 4. Connect
            await esploader.main_fn()

            // 5. Download Firmware Files via changelog
            setDialogState({
                showConnecting: true,
                text: 'Downloading firmware...',
            })
            const flashFilesData = await fetchFirmwareFiles(firmwareCheck)

            // 6. Erase
            setDialogState({
                showConnecting: true,
                text: 'Erasing chip (this might take a while)...',
            })
            await esploader.erase_flash()

            // 7. Write
            await esploader.write_flash({
                fileArray: flashFilesData,
                flash_size: 'keep',
                reportProgress: (fileIndex, written, total) => {
                    let progress = Math.round((written / total) * 100)
                    setDialogState({
                        showConnecting: true,
                        text: `Updating firmware - ${progress}%`,
                    })
                },
                calculateMD5Hash: (image) =>
                    CryptoJS.MD5(CryptoJS.enc.Latin1.parse(image)),
            })

            // 8. Cleanup
            await transport.disconnect()
            sessionStorage.removeItem('firmwareUpdateInProgress')

            // 9. Success & Reconnect Instructions
            setDialogState({
                showConnecting: false,
                showReconnectDialog: true,
                text: 'Firmware update complete!\n\nPlease disconnect and reconnect your device in NORMAL MODE.\nClick OK.',
                reconnectOptions: [
                    {
                        text: 'OK',
                        do: () => {
                            setDialogState((prev) => ({
                                ...prev,
                                showReconnectDialog: false,
                            }))
                        },
                    },
                ],
            })
        } catch (error) {
            console.error(error)
            sessionStorage.removeItem('firmwareUpdateInProgress')
            setDialogState({ showConnecting: false, text: '' })
            alert(`Firmware update failed: ${error.message}`)
        }
    }

    // --- WEB AGENT HANDLERS ---
    const handleWebAgentDownload = async () => {
        // Check if agent is already running
        if (props.socketData?.isAgentConnected) {
            // If agent is already connected, just show the dialog to inform user
            props.showWebAgentDialog()
            return
        }

        // If agent is not running, show the dialog to prompt download
        props.showWebAgentDialog()
    }

    const handleWebAgentInstall = async () => {
        props.setWebAgentInstalling()
        await downloadWebAgent()
    }

    const handleWebAgentStop = () => {
        props.hideWebAgentDialog()
    }

    const handleWebAgentCancel = () => {
        props.hideWebAgentDialog()
    }

    // --- ASSET UPDATE HANDLERS ---
    const handleAssetUpdate = () => {
        const socketCheck = checkSocketAvailability(props.socketData)
        if (!socketCheck.available) {
            if (
                window.confirm(
                    `${socketCheck.reason}\n\nDo you want to download the Web Agent?`
                )
            ) {
                handleWebAgentDownload()
            }
            return
        }
        setShowAssetUpdateDialog(true)
    }

    const handleAssetUpdateConfirm = async () => {
        setShowAssetUpdateDialog(false)
        const connectedDevice = sessionStorage.getItem('connectedDevice')
        const deviceVersion = sessionStorage.getItem('deviceVersion')

        if (!connectedDevice || !deviceVersion) {
            alert('Device info not found. Please connect your device first.')
            return
        }

        const deviceFolder =
            connectedDevice === 'Humanoid'
                ? 'zing'
                : connectedDevice.toLowerCase()

        let prefix = ''
        if (connectedDevice === 'Humanoid') prefix = 'ZG'
        else if (connectedDevice === 'Hexapod') prefix = 'CW'
        else if (connectedDevice === 'Ace') prefix = 'PC'

        const folderTag = prefix + deviceVersion.substring(0, 3)

        setFirmwareProgress({
            isOpen: true,
            stage: 'starting',
            message: 'Starting asset update...',
            percent: 0,
        })

        try {
            const updateResult = await checkAndUpdateMetaYaml({
                socket: props.socketData.socket,
                folderTag: folderTag,
                deviceFolder: deviceFolder,
                onProgress: (progress) => {
                    setFirmwareProgress({
                        isOpen: true,
                        stage: progress.stage,
                        message: progress.message,
                        percent: progress.percent || null,
                    })
                },
            })

            if (updateResult.success) {
                setFirmwareProgress({
                    isOpen: true,
                    stage: 'complete',
                    message: updateResult.updated
                        ? `Assets updated successfully! ${updateResult.filesUploaded} files uploaded.`
                        : 'Assets are already up to date.',
                    percent: 100,
                })
                setTimeout(() => {
                    setFirmwareProgress({
                        isOpen: false,
                        stage: '',
                        message: '',
                        percent: null,
                    })
                }, 3000)
            } else {
                setFirmwareProgress({
                    isOpen: true,
                    stage: 'error',
                    message: `Asset update failed: ${updateResult.error}`,
                    percent: null,
                })
            }
        } catch (error) {
            setFirmwareProgress({
                isOpen: true,
                stage: 'error',
                message: `Error: ${error.message}`,
                percent: null,
            })
        }
    }

    const handleAssetUpdateCancel = () => {
        setShowAssetUpdateDialog(false)
    }

    const handleCloseProgressModal = () => {
        setFirmwareProgress({
            isOpen: false,
            stage: '',
            message: '',
            percent: null,
        })
    }

    useEffect(() => {
        let assetsToBeCached = [
            renderImage('backBtn'),
            renderImage('firmwareUpdate'),
            renderImage('AccountSetting'),
            renderImage('aboutApplication'),
            renderImage('operationModes'),
        ]
        cacheAssets(assetsToBeCached)
    }, [])
    // console.log(
    //     'hai:',
    //     getBrand().slug !== 'default'
    //         ? getLogo('compact')
    //         : renderImage('yuduLogo')
    // )
    // console.log('logo:', getLogo('compact'))
    // Check for all updates when entering Advanced Settings
    useEffect(() => {
        const checkForUpdates = async () => {
            const currentDevice = sessionStorage.getItem('connectedDevice')
            const currentVersion = sessionStorage.getItem('deviceVersion')

            if (!currentDevice || !currentVersion) {
                console.log('No device connected, skipping update checks')
                return
            }

            let hasFirmwareUpdate = false
            let hasWebAgentUpdate = false
            let hasAssetUpdate = false

            // 1. Check Firmware Update
            try {
                const firmwareCheck = await checkFirmwareVersion(
                    getVersionObjNameKey(currentDevice),
                    currentVersion
                )

                if (firmwareCheck.updateAvailable) {
                    console.log('Firmware update available:', firmwareCheck)
                    hasFirmwareUpdate = true
                    props.setUserIgnoredFirmwareUpdate()
                }
            } catch (error) {
                console.error('Error checking firmware version:', error)
            }

            // 2. Check Web Agent
            const socketAvailability = checkSocketAvailability(props.socketData)
            if (!socketAvailability.available) {
                console.log(
                    'Web Agent not available:',
                    socketAvailability.reason
                )
                hasWebAgentUpdate = true
                props.setUserIgnoredWebAgent()
            }

            // 3. Check Asset Update
            if (
                props.socketData?.isAgentConnected &&
                props.socketData?.socket
            ) {
                try {
                    const deviceFolderMap = {
                        Humanoid: 'zing',
                        Hexapod: 'crawlee',
                        Ace: 'playcomputer',
                    }
                    const deviceFolder =
                        deviceFolderMap[currentDevice] || 'zing'

                    const assetUpdateResult = await checkAndUpdateMetaYaml({
                        socket: props.socketData.socket,
                        folderTag: currentVersion?.substring(0, 5) || 'ZG2.1',
                        deviceFolder,
                        checkOnly: true,
                    })

                    if (assetUpdateResult?.updateAvailable) {
                        console.log(
                            'Asset update available:',
                            assetUpdateResult
                        )
                        hasAssetUpdate = true
                        props.setUserIgnoredAssetUpdate()
                    }
                } catch (error) {
                    console.error('Error checking asset update:', error)
                }
            }

            // Update local state for notification badges
            setUpdateAvailability({
                firmwareUpdate: hasFirmwareUpdate,
                webAgent: hasWebAgentUpdate,
                assetUpdate: hasAssetUpdate,
            })
        }

        checkForUpdates()
    }, [])

    // Monitor Web Agent connection status in real-time
    useEffect(() => {
        const isAgentConnected = props.socketData?.isAgentConnected

        setUpdateAvailability((prev) => ({
            ...prev,
            webAgent: !isAgentConnected,
        }))
    }, [props.socketData?.isAgentConnected])

    const SettingCard = ({
        img,
        text,
        onClick,
        className,
        showNotification,
        notificationText,
    }) => (
        <div className={AdvanceSettingStyle.Card_Wrapper} onClick={onClick}>
            {/* Icon positioned outside the rotated box */}
            <div className={AdvanceSettingStyle.Card_Icon_Container}>
                <img
                    alt={text}
                    className={`${AdvanceSettingStyle.Card_Image} ${
                        className || ''
                    }`}
                    src={renderImage(img)}
                />
                {showNotification && (
                    <div
                        className={AdvanceSettingStyle.Card_Notification_Badge}
                    >
                        <Info style={{ color: '#fff', fontSize: 'inherit' }} />
                        {notificationText && (
                            <span className={AdvanceSettingStyle.badge_tooltip}>
                                {notificationText}
                            </span>
                        )}
                    </div>
                )}
            </div>

            {/* Rotated box with text */}
            <div className={AdvanceSettingStyle.Card_Item}>
                <div className={AdvanceSettingStyle.Card_Text_Container}>
                    <p className={AdvanceSettingStyle.Card_Text_P}>
                        {text.split('\n').map((line, i) => (
                            <span key={i}>
                                {line}
                                <br />
                            </span>
                        ))}
                    </p>
                </div>
            </div>
        </div>
    )

    return (
        <>
            <div className={AdvanceSettingStyle.Main_Select}>
                <div className={AdvanceSettingStyle.Select_Header}>
                    <div>
                        <img
                            className={AdvanceSettingStyle.Back_Btn}
                            src={renderImage('backBtn')}
                            onClick={gobackUrl}
                            alt="Back"
                        ></img>
                    </div>
                    <div style={{ position: 'relative' }}>
                        <p className={AdvanceSettingStyle.text}>
                            Advanced Settings
                        </p>
                    </div>
                </div>

                <div className={AdvanceSettingStyle.Container_Body}>
                    {isHumanoid3 && (
                        <div className={AdvanceSettingStyle.buttonParent}>
                            <div
                                className={`${AdvanceSettingStyle.button} ${activeSwipeBtn ? AdvanceSettingStyle.active : ''}`}
                                onClick={() =>
                                    setActiveSwipeBtn(!activeSwipeBtn)
                                }
                            >
                                <span
                                    className={AdvanceSettingStyle.swipeBtnText}
                                >
                                    Demo Mode
                                </span>

                                <div className={AdvanceSettingStyle.circle} />
                            </div>
                        </div>
                    )}

                    <div
                        className={AdvanceSettingStyle.Card_Row}
                        ref={cardRowRef}
                    >
                        <SettingCard
                            img="AccountSetting"
                            text="Account&#10;Setting"
                            onClick={AccountSetting}
                        />

                        {/* FIRMWARE UPDATE - Now calls the new logic */}
                        <SettingCard
                            img="firmwareUpdate"
                            text="Firmware&#10;Update"
                            onClick={handleFirmwareCheck}
                            showNotification={updateAvailability.firmwareUpdate}
                            notificationText="New firmware available"
                        />

                        <SettingCard
                            img="webAgent"
                            text="Download&#10;Web Agent"
                            onClick={handleWebAgentDownload}
                            showNotification={updateAvailability.webAgent}
                            notificationText="Web Agent needed"
                        />
                        <SettingCard
                            img="assets"
                            text="Update&#10;Assets"
                            onClick={handleAssetUpdate}
                            showNotification={updateAvailability.assetUpdate}
                            notificationText="Asset updates available"
                        />
                        {isHumanoid2 && (
                            <SettingCard
                                img="operationModes"
                                text="Operation&#10;Modes"
                                onClick={operationModeHandel}
                            />
                        )}

                        {isHumanoid3 && (
                            <SettingCard
                                img="adjustPersonaIcon"
                                text="Adjust&#10;Persona"
                                onClick={personaClick}
                            />
                        )}

                        <SettingCard
                            img="aboutApplication"
                            text="Learn more&#10;About us"
                            onClick={aboutClick}
                        />
                    </div>
                </div>
            </div>

            {/* --- DIALOGS --- */}

            {/* 1. Firmware Check Dialog (Version Info) */}
            <FirmwareUpdateDialog
                isOpen={props.firmwareUpdateDialog?.isOpen}
                currentVersion={props.firmwareUpdateDialog?.currentVersion}
                latestVersion={props.firmwareUpdateDialog?.latestVersion}
                deviceName={props.firmwareUpdateDialog?.deviceName}
                onUpdate={handleFirmwareUpdateStart}
                mandatory={props.firmwareUpdateDialog?.firmwareCheck?.mandatory}
                onSkip={handleFirmwareUpdateSkip}
                onClose={handleFirmwareUpdateClose}
            />

            {/* 2. Generic Modal for Boot Instructions, Connecting, Reconnect */}
            <DialogModal
                mismatchShow={dialogState.showBootModeDialog}
                text={dialogState.text}
                showCloseBtn={false}
                optionsToSelect={dialogState.bootModeOptions || []}
            />

            <DialogModal
                connection={dialogState.showConnecting}
                text={dialogState.text}
                optionsToSelect={[]} // No buttons while connecting
            />

            <DialogModal
                mismatchShow={dialogState.showReconnectDialog}
                text={dialogState.text}
                showCloseBtn={false}
                optionsToSelect={dialogState.reconnectOptions || []}
            />

            {/* 3. Asset Update Dialogs */}
            <AssetUpdateDialog
                isOpen={showAssetUpdateDialog}
                onInstall={handleAssetUpdateConfirm}
                onSkip={handleAssetUpdateCancel}
            />

            <FirmwareAssetsUpdateModal
                isOpen={firmwareProgress.isOpen}
                stage={firmwareProgress.stage}
                message={firmwareProgress.message}
                percent={firmwareProgress.percent}
                onClose={handleCloseProgressModal}
            />

            <WebAgentDialog
                isOpen={props.webAgentDialog?.isOpen}
                onInstall={handleWebAgentInstall}
                onStop={handleWebAgentStop}
                onCancel={handleWebAgentCancel}
                isInstalling={props.webAgentDialog?.isInstalling}
                isAgentConnected={props.socketData?.isAgentConnected}
            />
        </>
    )
}

const mapStateToProps = (state) => {
    return {
        socketData: state.socketData,
        webAgentDialog: state.webAgentDialog,
        firmwareUpdateDialog: state.firmwareUpdateDialog, // Need this from Redux
        worker: state.worker,
    }
}

const mapDispatchToProps = (dispatch) => {
    return {
        showWebAgentDialog: () => {
            dispatch({ type: 'SHOW_WEB_AGENT_DIALOG' })
        },
        hideWebAgentDialog: () => {
            dispatch({ type: 'HIDE_WEB_AGENT_DIALOG' })
        },
        setWebAgentInstalling: () => {
            dispatch({ type: 'SET_WEB_AGENT_INSTALLING' })
        },

        webSerialDisconnect: () => dispatch(webSerialDisconnect()),

        // Firmware Dialog Actions
        showFirmwareUpdateDialog: (payload) => {
            dispatch({ type: 'SHOW_FIRMWARE_UPDATE_DIALOG', payload })
        },
        hideFirmwareUpdateDialog: () => {
            dispatch({ type: 'HIDE_FIRMWARE_UPDATE_DIALOG' })
        },

        // User Ignored Update Actions
        setUserIgnoredWebAgent: () => {
            dispatch({ type: 'USER_IGNORED_WEB_AGENT' })
        },
        setUserIgnoredAssetUpdate: () => {
            dispatch({ type: 'USER_IGNORED_ASSET_UPDATE' })
        },
        setUserIgnoredFirmwareUpdate: () => {
            const currentDevice = sessionStorage.getItem('connectedDevice')
            const currentVersion = sessionStorage.getItem('deviceVersion')
            const firmwareKey = `${currentDevice}:${currentVersion}`
            dispatch({
                type: 'USER_IGNORED_FIRMWARE_UPDATE',
                payload: firmwareKey,
            })
        },
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(AdvanceSettingV1)
